

import katex from "katex"
import "katex/dist/katex.min.css"

// Quiz format types
export const QUIZ_FORMATS = {
  QUIZ: "quiz",
  PRACTICE: "practice",
  EXAM: "exam",
}

// Time to question mapping for different formats
export const TIME_TO_QUESTIONS = {
  "15 minutes": 3,
  "30 minutes": 6,
  "45 minutes": 9,
  "1 hour": 12,
  "1.5 hours": 18,
  "2 hours": 24,
  "3 hours": 36,
}

/**
 * Parse mathematical formulas using KaTeX
 * @param {string} content - Content with LaTeX formulas
 * @returns {string} HTML with rendered formulas
 */
export function MathFormulaParse(content) {
  if (!content) return ""

  // Handle object content
  if (typeof content === "object") {
    if (content.Parts && Array.isArray(content.Parts)) {
      const joinedContent = content.Parts.map((part) => {
        let markingScheme = part.MarkingScheme
        if (markingScheme && typeof markingScheme === "object") {
          try {
            markingScheme = JSON.stringify(markingScheme, null, 2)
          } catch (e) {
            markingScheme = "See detailed marking scheme in solution"
          }
        }
        return [part.Solution, markingScheme ? `\nMarking Scheme: ${markingScheme}\n` : ""].filter(Boolean).join("\n")
      }).join("\n")
      return MathFormulaParse(joinedContent)
    }

    try {
      content = JSON.stringify(content, null, 2)
    } catch (e) {
      return "Error: Could not process content"
    }
  }

  // Pre-process LaTeX delimiters
  content = content.replace(/\\\[\s*/g, "\\[")
  content = content.replace(/\s*\\\]/g, "\\]")
  content = content.replace(/\\\(\s*/g, "\\(")
  content = content.replace(/\\\\\\\(\s*/g, "\\(\\\\")
  content = content.replace(/\s*\\\)/g, "\\)")

  const regex =
    /(\\$[\s\S]*?\\$)|(\\$$[\s\S]*?\\$$)|(\\\[[\s\S]*?\\\])|(\\\([\s\S]*?\\\))|(\$\$[\s\S]*?\$\$)|(\$[\s\S]*?\$)|(\$[\s\S]*?\$)|(\\begin\{array\}[\s\S]*?\\end\{array\})|(\\textit\{.*?\})|(\\textbf\{.*?\})/g

  let displayContent = ""
  let lastIndex = 0
  let match

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      displayContent += content.substring(lastIndex, match.index).replace(/\n/g, "<br />")
    }

    const part = match[0]
    try {
      const isDisplayMode = part.startsWith("\\[") || part.startsWith("$$")

      const mathContent = part
        .replace(/\\\[|\\\]/g, "") // Remove \[ and \]
        .replace(/\\\(|\\\)/g, "") // Remove \( and \)
        .replace(/\\$$|\\$$/g, "") // Remove $$ and $$
        .replace(/\$\$/g, "") // Remove $$
        .replace(/\$/g, "") // Remove $
        .trim();

      const renderedMath = katex.renderToString(mathContent, {
        throwOnError: false,
        displayMode: isDisplayMode,
        output: "html",
      })

      displayContent += renderedMath
    } catch (e) {
      console.error("Error rendering LaTeX:", e, part)
      displayContent += part
    }

    lastIndex = match.index + part.length
  }

  if (lastIndex < content.length) {
    displayContent += content.substring(lastIndex).replace(/\n/g, "<br />")
  }

  return displayContent
}

/**
 * Get format-specific configuration
 * @param {string} format - Quiz format type
 * @returns {Object} Format configuration
 */
export function getFormatConfig(format) {
  const configs = {
    [QUIZ_FORMATS.QUIZ]: {
      title: "MATHEMATICS ASSESSMENT",
      subtitle: "Question Paper",
      examCode: "MATH/QP/001",
      duration: "Time allowed: As specified",
      showAnswerSpace: true,
      answerSpaceLines: 25, // Increased for more space
      showStudentInfo: true,
      showInstructions: false,
      instructions: [
        "Answer ALL questions.",
        "Write your answers in the spaces provided.",
        "Show all your working clearly.",
        "Marks may be given for correct working even if the final answer is wrong.",
        "Unless otherwise stated, answers should be given exactly or to 3 significant figures.",
        "The use of an approved scientific calculator is expected, where appropriate.",
      ],
      headerColor: "#000000",
      accentColor: "#333333",
      backgroundColor: "#ffffff",
    },
    [QUIZ_FORMATS.PRACTICE]: {
      title: "MATHEMATICS PRACTICE SESSION",
      subtitle: "Practice Question Paper",
      examCode: "MATH/PRAC/001",
      duration: "Time allowed: As specified",
      showAnswerSpace: false,
      answerSpaceLines: 25, // Increased for more space
      showStudentInfo: false,
      showInstructions: false,
      instructions: [
        "Answer ALL questions.",
        "Write your answers in the spaces provided.",
        "Show all your working clearly.",
        "This is a practice session - take your time to understand each concept.",
        "Marks may be given for correct working even if the final answer is wrong.",
        "Unless otherwise stated, answers should be given exactly or to 3 significant figures.",
        "The use of an approved scientific calculator is expected, where appropriate.",
        "Solutions are provided for learning purposes.",
      ],
      headerColor: "#000000",
      accentColor: "#333333",
      backgroundColor: "#ffffff",
    },
    [QUIZ_FORMATS.EXAM]: {
      title: "MATHEMATICS EXAMINATION",
      subtitle: "Question Paper",
      examCode: "MATH/EXAM/001",
      duration: "Time allowed: As specified",
      showAnswerSpace: true,
      answerSpaceLines: 25, // Increased for more space
      showStudentInfo: true,
      showInstructions: true,
      instructions: [
        "Answer ALL questions.",
        "Write your answers in the spaces provided.",
        "Show all your working clearly.",
        "Marks may be given for correct working even if the final answer is wrong.",
        "Unless otherwise stated, answers should be given exactly or to 3 significant figures.",
        "The use of an approved scientific calculator is expected, where appropriate.",
        "No additional materials are allowed unless specified.",
        "This is a formal examination - manage your time carefully.",
      ],
      headerColor: "#000000",
      accentColor: "#333333",
      backgroundColor: "#ffffff",
    },
  }

  return configs[format] || configs[QUIZ_FORMATS.QUIZ]
}

/**
 * Create formal exam header
 * @param {Object} options - Header options
 * @returns {HTMLElement} Header element
 */
export function createDocumentHeader(options = {}) {
  const {
    title = "MATHEMATICS ASSESSMENT",
    subtitle = "Question Paper",
    questionCount = 0,
    includeSolution = false,
    format = QUIZ_FORMATS.QUIZ,
    metadata = {},
  } = options

  const config = getFormatConfig(format)

  const header = document.createElement("header")
  header.style.textAlign = "center"
  header.style.marginBottom = "40px"
  header.style.paddingBottom = "20px"
  header.style.borderBottom = "2px solid #000000"

  // Main title
  const mainTitle = document.createElement("h1")
  mainTitle.style.fontSize = "24px"
  mainTitle.style.fontWeight = "bold"
  mainTitle.style.color = "#000000"
  mainTitle.style.margin = "0 0 10px 0"
  mainTitle.style.letterSpacing = "2px"
  mainTitle.textContent = metadata.examName || config.title

  // Subtitle
  const subTitle = document.createElement("h2")
  subTitle.style.fontSize = "18px"
  subTitle.style.fontWeight = "normal"
  subTitle.style.color = "#000000"
  subTitle.style.margin = "0 0 20px 0"
  subTitle.textContent = config.subtitle

  // School name if provided
  if (metadata.schoolName) {
    const schoolName = document.createElement("h3")
    schoolName.style.fontSize = "16px"
    schoolName.style.fontWeight = "normal"
    schoolName.style.color = "#000000"
    schoolName.style.margin = "0 0 15px 0"
    schoolName.textContent = metadata.schoolName
    header.appendChild(schoolName)
  }
  header.appendChild(mainTitle)
  header.appendChild(subTitle)

  // Exam details grid
  const examDetails = document.createElement("div")
  examDetails.style.display = "grid"
  examDetails.style.gridTemplateColumns = "1fr 1fr 1fr"
  examDetails.style.gap = "20px"
  examDetails.style.marginTop = "20px"
  examDetails.style.fontSize = "14px"
  examDetails.style.fontWeight = "bold"

  const examCode = document.createElement("div")
  examCode.innerHTML = `Paper Code: ${config.examCode}`

  const examDate = document.createElement("div")
  examDate.innerHTML = `Date: ${new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })}`

  const examDuration = document.createElement("div")
  examDuration.innerHTML = config.duration

  examDetails.appendChild(examCode)
  examDetails.appendChild(examDate)
  examDetails.appendChild(examDuration)
  header.appendChild(examDetails)

  // Additional info
  const additionalInfo = document.createElement("div")
  additionalInfo.style.marginTop = "15px"
  additionalInfo.style.fontSize = "12px"
  additionalInfo.style.color = "#666"

  const questionInfo = document.createElement("p")
  questionInfo.style.margin = "5px 0"
  questionInfo.innerHTML = `Total Questions: ${questionCount} | Solutions: ${includeSolution ? "Included" : "Not included"}`
  additionalInfo.appendChild(questionInfo)

  // Author name if provided
  if (metadata.authorName) {
    const authorInfo = document.createElement("p")
    authorInfo.style.margin = "5px 0"
    authorInfo.innerHTML = `Prepared by: ${metadata.authorName}`
    additionalInfo.appendChild(authorInfo)
  }

  additionalInfo.appendChild(questionInfo)

  header.appendChild(additionalInfo)

  return header
}

/**
 * Create formal student information section
 * @param {string} format - Quiz format
 * @returns {HTMLElement} Student info element
 */
export function createStudentInfoSection(format) {
  const config = getFormatConfig(format)

  const studentSection = document.createElement("div")
  studentSection.style.marginBottom = "40px"
  studentSection.style.padding = "25px"
  studentSection.style.border = "2px solid #000000"
  studentSection.style.borderRadius = "0px" // Formal, no rounded corners
  studentSection.style.backgroundColor = "#ffffff"

  const studentHeader = document.createElement("h3")
  studentHeader.style.fontSize = "16px"
  studentHeader.style.fontWeight = "bold"
  studentHeader.style.marginBottom = "20px"
  studentHeader.style.color = "#000000"
  studentHeader.style.textAlign = "center"
  studentHeader.style.textTransform = "uppercase"
  studentHeader.style.letterSpacing = "1px"
  studentHeader.textContent = "CANDIDATE INFORMATION"

  const studentInfoGrid = document.createElement("div")
  studentInfoGrid.style.display = "grid"
  studentInfoGrid.style.gridTemplateColumns = "1fr 1fr"
  studentInfoGrid.style.gap = "25px"

  const fields = [
    { label: "Candidate Name:", id: "student-name", width: "300px" },
    { label: "Candidate Number:", id: "student-number", width: "200px" },
    { label: "Centre Name:", id: "centre-name", width: "300px" },
    { label: "Centre Number:", id: "centre-number", width: "200px" },
    { label: "Date:", id: "exam-date", width: "200px" },
    { label: "Signature:", id: "signature", width: "200px" },
  ]

  fields.forEach((field, index) => {
    const fieldDiv = document.createElement("div")
    fieldDiv.style.marginBottom = "15px"

    const label = document.createElement("label")
    label.style.display = "block"
    label.style.fontWeight = "bold"
    label.style.marginBottom = "8px"
    label.style.color = "#000000"
    label.style.fontSize = "14px"
    label.textContent = field.label

    const inputLine = document.createElement("div")
    inputLine.style.borderBottom = "2px solid #000000"
    inputLine.style.padding = "8px 0"
    inputLine.style.minHeight = "30px"
    inputLine.style.width = field.width
    inputLine.id = field.id

    fieldDiv.appendChild(label)
    fieldDiv.appendChild(inputLine)
    studentInfoGrid.appendChild(fieldDiv)
  })

  studentSection.appendChild(studentHeader)
  studentSection.appendChild(studentInfoGrid)

  return studentSection
}

/**
 * Create formal instructions section
 * @param {string} format - Quiz format
 * @param {string} customInstructions - Custom instructions
 * @returns {HTMLElement} Instructions element
 */
export function createInstructionsSection(format, customInstructions = null) {
  const config = getFormatConfig(format)

  const instructions = document.createElement("div")
  instructions.style.backgroundColor = "#f8f9fa"
  instructions.style.padding = "25px"
  instructions.style.border = "2px solid #000000"
  instructions.style.marginBottom = "40px"
  instructions.style.fontSize = "14px"
  instructions.style.lineHeight = "1.6"

  const instructionsTitle = document.createElement("h3")
  instructionsTitle.style.margin = "0 0 20px 0"
  instructionsTitle.style.fontSize = "16px"
  instructionsTitle.style.fontWeight = "bold"
  instructionsTitle.style.color = "#000000"
  instructionsTitle.style.textAlign = "center"
  instructionsTitle.style.textTransform = "uppercase"
  instructionsTitle.style.letterSpacing = "1px"
  instructionsTitle.textContent = "INSTRUCTIONS TO CANDIDATES"

  const instructionsList = document.createElement("ol")
  instructionsList.style.margin = "0"
  instructionsList.style.paddingLeft = "20px"
  instructionsList.style.color = "#000000"

  const instructionsToUse = customInstructions ? [customInstructions] : config.instructions

  instructionsToUse.forEach((instruction) => {
    const listItem = document.createElement("li")
    listItem.style.marginBottom = "8px"
    listItem.style.fontWeight = "500"
    listItem.textContent = instruction
    instructionsList.appendChild(listItem)
  })

  instructions.appendChild(instructionsTitle)
  instructions.appendChild(instructionsList)

  return instructions
}

/**
 * Create metadata section
 * @param {Array} questions - Questions array
 * @returns {HTMLElement} Metadata element
 */
export function createMetadataSection(questions) {
  if (!questions || questions.length === 0) {
    return null
  }

  const subjects = [...new Set(questions.map((q) => q.subject).filter(Boolean))]
  const examBoards = [...new Set(questions.map((q) => q.examBoard).filter(Boolean))]
  const syllabusCodes = [...new Set(questions.map((q) => q.syllabusCode).filter(Boolean))]

  if (subjects.length === 0 && examBoards.length === 0 && syllabusCodes.length === 0) {
    return null
  }

  const metadataSection = document.createElement("div")
  metadataSection.style.marginBottom = "40px"
  metadataSection.style.padding = "20px"
  metadataSection.style.backgroundColor = "#f8f9fa"
  metadataSection.style.border = "1px solid #000000"
  metadataSection.style.fontSize = "14px"

  const metadataTitle = document.createElement("h3")
  metadataTitle.style.margin = "0 0 15px 0"
  metadataTitle.style.fontSize = "16px"
  metadataTitle.style.fontWeight = "bold"
  metadataTitle.style.textAlign = "center"
  metadataTitle.style.textTransform = "uppercase"
  metadataTitle.style.letterSpacing = "1px"
  metadataTitle.textContent = "ASSESSMENT DETAILS"

  metadataSection.appendChild(metadataTitle)

  if (subjects.length > 0) {
    const subjectInfo = document.createElement("p")
    subjectInfo.style.margin = "8px 0"
    subjectInfo.style.fontWeight = "bold"
    subjectInfo.innerHTML = `<strong>Subject(s):</strong> ${subjects.join(", ")}`
    metadataSection.appendChild(subjectInfo)
  }

  if (examBoards.length > 0) {
    const boardInfo = document.createElement("p")
    boardInfo.style.margin = "8px 0"
    boardInfo.style.fontWeight = "bold"
    boardInfo.innerHTML = `<strong>Exam Board(s):</strong> ${examBoards.join(", ")}`
    metadataSection.appendChild(boardInfo)
  }

  if (syllabusCodes.length > 0) {
    const codeInfo = document.createElement("p")
    codeInfo.style.margin = "8px 0"
    codeInfo.style.fontWeight = "bold"
    codeInfo.innerHTML = `<strong>Syllabus Code(s):</strong> ${syllabusCodes.join(", ")}`
    metadataSection.appendChild(codeInfo)
  }

  return metadataSection
}

/**
 * Create generous answer space for a question
 * @param {string} format - Quiz format
 * @param {number} customLines - Custom number of lines
 * @returns {HTMLElement} Answer space element
 */
export function createAnswerSpace(format, customLines = null) {
  const config = getFormatConfig(format)

  if (!config.showAnswerSpace) {
    return null
  }

  const answerSpace = document.createElement("div")
  answerSpace.style.marginTop = "30px"
  answerSpace.style.marginBottom = "40px"
  answerSpace.style.pageBreakInside = "avoid"

  const answerTitle = document.createElement("h4")
  answerTitle.style.fontSize = "14px"
  answerTitle.style.fontWeight = "bold"
  answerTitle.style.marginBottom = "20px"
  answerTitle.style.color = "#000000"
  answerTitle.style.textTransform = "uppercase"
  answerTitle.textContent = "ANSWER:"
  answerSpace.appendChild(answerTitle)

  const lineCount = customLines || config.answerSpaceLines

  // Create answer area with more generous spacing
  for (let i = 0; i < lineCount + 10; i++) {
    const line = document.createElement("div")
    line.style.borderBottom = "1px solid #cccccc"
    line.style.height = "35px" // Increased line height
    line.style.width = "100%"
    line.style.marginBottom = "2px"
    answerSpace.appendChild(line)
  }

  // Add a working space note
  const workingNote = document.createElement("div")
  workingNote.style.marginTop = "20px"
  workingNote.style.fontSize = "12px"
  workingNote.style.color = "#666666"
  workingNote.style.fontStyle = "italic"
  workingNote.textContent = "Use this space for your working and final answer."
  answerSpace.appendChild(workingNote)

  return answerSpace
}

/**
 * Create a formatted question element with generous spacing
 * @param {Object} question - Question data
 * @param {number} index - Question index
 * @param {Object} options - Formatting options
 * @returns {HTMLElement} Question element
 */
export function createQuestionElement(question, index, options = {}) {
  const {
    format = QUIZ_FORMATS.QUIZ,
    includeSolution = false,
    showQuestionImages = true,
    customAnswerLines = null,
  } = options

  const config = getFormatConfig(format)

  const questionDiv = document.createElement("div")
  questionDiv.style.breakInside = "avoid"
  questionDiv.style.pageBreakInside = "avoid"
  questionDiv.style.padding = "30px"
  questionDiv.style.border = "2px solid #000000"
  questionDiv.style.marginBottom = "40px"
  questionDiv.style.backgroundColor = "#ffffff"
  questionDiv.style.minHeight = "800px" // Ensure generous space per question

  // Question header
  const questionHeader = document.createElement("div")
  questionHeader.style.display = "flex"
  questionHeader.style.justifyContent = "space-between"
  questionHeader.style.alignItems = "center"
  questionHeader.style.marginBottom = "25px"
  questionHeader.style.paddingBottom = "15px"
  questionHeader.style.borderBottom = "2px solid #000000"

  const questionNumber = document.createElement("h2")
  questionNumber.style.fontSize = "20px"
  questionNumber.style.fontWeight = "bold"
  questionNumber.style.color = "#000000"
  questionNumber.style.margin = "0"
  questionNumber.textContent = `${index + 1}.`

  const questionMeta = document.createElement("div")
  questionMeta.style.fontSize = "12px"
  questionMeta.style.color = "#666666"
  questionMeta.style.textAlign = "right"

  if (question.subject || question.yearOfExam) {
    const metaText = document.createElement("span")
    metaText.innerHTML = `${question.subject || ""} ${question.yearOfExam ? "| " + question.yearOfExam : ""}`
    questionMeta.appendChild(metaText)
  }

  questionHeader.appendChild(questionNumber)
  questionHeader.appendChild(questionMeta)
  questionDiv.appendChild(questionHeader)

  // Question title
  if (question.questionTitle) {
    const questionTitle = document.createElement("h3")
    questionTitle.style.fontSize = "16px"
    questionTitle.style.fontWeight = "bold"
    questionTitle.style.marginBottom = "20px"
    questionTitle.style.color = "#000000"
    questionTitle.textContent = question.questionTitle
    questionDiv.appendChild(questionTitle)
  }

  // Question images
  if (showQuestionImages && question.questionImages && question.questionImages.length > 0) {
    const imagesContainer = document.createElement("div")
    imagesContainer.style.marginBottom = "25px"
    imagesContainer.style.textAlign = "center"

    question.questionImages.forEach((imageSrc, imgIndex) => {
      const img = document.createElement("img")
      img.src = imageSrc
      img.alt = `Question ${index + 1} - Image ${imgIndex + 1}`
      img.style.maxWidth = "100%"
      img.style.height = "auto"
      img.style.marginBottom = "15px"
      img.style.border = "1px solid #cccccc"
      imagesContainer.appendChild(img)
    })

    questionDiv.appendChild(imagesContainer)
  }

  // Question content
  const description = document.createElement("div")
  description.style.fontSize = "16px"
  description.style.lineHeight = "1.8"
  description.style.marginBottom = "30px"
  description.style.color = "#000000"
  description.style.padding = "20px"
  description.style.backgroundColor = "#f9f9f9"
  description.style.border = "1px solid #cccccc"
  description.innerHTML = MathFormulaParse(question.questionContent)
  questionDiv.appendChild(description)

  // Answer space (always show for formal format)
  const answerSpace = createAnswerSpace(format, customAnswerLines)
  if (answerSpace) {
    questionDiv.appendChild(answerSpace)
  }

  // Solution (if included) - on separate section
  if (includeSolution && question.solutionContent) {
    // Add page break before solution
    const pageBreak = document.createElement("div")
    pageBreak.style.pageBreakBefore = "always"
    pageBreak.style.marginTop = "40px"
    questionDiv.appendChild(pageBreak)

    const solutionSection = document.createElement("div")
    solutionSection.style.marginTop = "40px"
    solutionSection.style.padding = "25px"
    solutionSection.style.border = "2px solid #000000"
    solutionSection.style.backgroundColor = "#f0f8ff"

    const solutionTitle = document.createElement("h4")
    solutionTitle.style.fontSize = "16px"
    solutionTitle.style.fontWeight = "bold"
    solutionTitle.style.marginBottom = "20px"
    solutionTitle.style.color = "#000000"
    solutionTitle.style.textAlign = "center"
    solutionTitle.style.textTransform = "uppercase"
    solutionTitle.textContent = `SOLUTION TO QUESTION ${index + 1}`
    solutionSection.appendChild(solutionTitle)

    const solution = document.createElement("div")
    solution.style.fontSize = "15px"
    solution.style.lineHeight = "1.6"
    solution.style.color = "#000000"
    solution.innerHTML = MathFormulaParse(question.solutionContent)
    solutionSection.appendChild(solution)

    questionDiv.appendChild(solutionSection)
  }

  return questionDiv
}

/**
 * Create document footer
 * @param {Object} options - Footer options
 * @returns {HTMLElement} Footer element
 */
export function createDocumentFooter(options = {}) {
  const { showPageNumbers = true } = options

  const footer = document.createElement("footer")
  footer.style.marginTop = "60px"
  footer.style.paddingTop = "20px"
  footer.style.borderTop = "2px solid #000000"
  footer.style.textAlign = "center"
  footer.style.fontSize = "12px"
  footer.style.color = "#666666"
  footer.style.fontWeight = "bold"

  if (showPageNumbers) {
    const pageText = document.createTextNode("Page ")
    footer.appendChild(pageText)

    const pageNumber = document.createElement("span")
    pageNumber.className = "pageNumber"
    pageNumber.textContent = "1"
    footer.appendChild(pageNumber)
  }

  return footer
}

/**
 * Generate complete formatted quiz document
 * @param {Array} questions - Questions array
 * @param {Object} options - Formatting options
 * @returns {HTMLElement} Complete document element
 */
export function generateFormattedQuiz(questions, options = {}) {
  const {
    format = QUIZ_FORMATS.QUIZ,
    includeSolution = false,
    title = null,
    subtitle = null,
    customInstructions = null,
    showMetadata = true,
    showStudentInfo = true,
    showQuestionImages = true,
    customAnswerLines = null,
    metadata = {},
  } = options

  const config = getFormatConfig(format)

  // Create main container
  const printContent = document.createElement("div")
  printContent.style.padding = "40px"
  printContent.style.maxWidth = "210mm" // A4 width
  printContent.style.margin = "0 auto"
  printContent.style.fontFamily = "Times New Roman, serif" // More formal font
  printContent.style.backgroundColor = "#fff"
  printContent.style.color = "#000"
  printContent.style.lineHeight = "1.6"

  // Add header with metadata
  const header = createDocumentHeader({
    title: title || config.title,
    subtitle: subtitle || config.subtitle,
    questionCount: questions.length,
    includeSolution,
    format,
    metadata: metadata,
  })
  printContent.appendChild(header)

  // Add student info section (always show for formal format)
  const studentInfo = createStudentInfoSection(format)
  if (studentInfo) {
    printContent.appendChild(studentInfo)
  }

  // Add instructions
  const instructions = createInstructionsSection(format, customInstructions)
  if (instructions) {
    printContent.appendChild(instructions)
  }

  // Add metadata
  if (showMetadata) {
    const metadataSection = createMetadataSection(questions)
    if (metadataSection) {
      printContent.appendChild(metadataSection)
    }
  }

  // Add page break before questions
  const questionsPageBreak = document.createElement("div")
  questionsPageBreak.style.pageBreakBefore = "always"
  printContent.appendChild(questionsPageBreak)

  // Add questions
  const questionsContainer = document.createElement("div")

  questions.forEach((question, index) => {
    if (question) {
      // Add page break before each question (except the first)
      if (index > 0) {
        const pageBreak = document.createElement("div")
        pageBreak.style.pageBreakBefore = "always"
        questionsContainer.appendChild(pageBreak)
      }

      const questionElement = createQuestionElement(question, index, {
        format,
        includeSolution,
        showQuestionImages,
        customAnswerLines,
      })
      questionsContainer.appendChild(questionElement)
    }
  })

  printContent.appendChild(questionsContainer)

  // Add footer without website references
  const footer = createDocumentFooter({
    showPageNumbers: true,
  })
  printContent.appendChild(footer)

  return printContent
}

/**
 * Print formatted quiz
 * @param {Array} questions - Questions array
 * @param {Object} options - Print options
 */
export function printFormattedQuiz(questions, options = {}) {
  if (!questions || questions.length === 0) {
    alert("No questions available to generate quiz")
    return
  }

  // If options is a string, assume it's the format
  if (typeof options === "string") {
    options = { format: options }
  }

  // If options is a number, assume it's questionCount
  if (typeof options === "number") {
    options = { questionCount: options }
  }

  // Handle the case where the second parameter is includeSolution (boolean)
  if (typeof options === "boolean") {
    options = { includeSolution: options }
  }

  // Generate the formatted document
  const printContent = generateFormattedQuiz(questions, options)

  // Add KaTeX CSS
  const katexLink = document.createElement("link")
  katexLink.rel = "stylesheet"
  katexLink.href = "https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css"
  katexLink.integrity = "sha384-GvrOXuhMATgEsSwCs4smul74iXGOixntxDyxJRn/bFFOLxvJ4o/3qnJD7Cg0GmHbQ"
  katexLink.crossOrigin = "anonymous"
  document.head.appendChild(katexLink)

  // Add custom styles for formal exam format
  const customStyles = document.createElement("style")
  customStyles.textContent = `
    .katex {
      font-size: 1.1em;
      margin: 0 0.2em;
    }
    
    .katex-display {
      margin: 1.5em 0;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 0.5em 0;
    }
    
    @media print {
      @page {
        margin: 20mm;
        size: A4;
      }
      body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
        font-family: "Times New Roman", serif;
      }
      .pageNumber:before {
        content: counter(page);
      }
      .pageNumber:after {
        content: ' of ' counter(pages);
      }
      .page-break {
        page-break-after: always;
      }
      .question-container {
        page-break-inside: avoid;
      }
      
      /* Ensure proper spacing for formal exam */
      h1, h2, h3, h4 {
        page-break-after: avoid;
      }
      
      /* Prevent orphaned lines */
      p {
        orphans: 3;
        widows: 3;
      }
    }
  `
  document.head.appendChild(customStyles)

  // Hide original content more comprehensively
  const elementsToHide = [
    document.getElementsByClassName("App max-h-screen flex flex-col")[0],
    document.getElementById("original-content"),
    document.querySelector(".min-h-screen.bg-gradient-to-br"), // QuizSetupWizard
    document.querySelector(".min-h-screen.bg-\\[\\#ebebeb\\]"), // QuestionSearch
    document.querySelector('[id="root"]'), // React root if present
  ]

  const hiddenElements = []

  elementsToHide.forEach((element) => {
    if (element && element.style.display !== "none") {
      element.style.display = "none"
      hiddenElements.push(element)
    }
  })

  // Add to body and print
  document.body.appendChild(printContent)

  // Print after a delay to ensure rendering
  setTimeout(() => {
    window.print()

    // Restore original content
    hiddenElements.forEach((element) => {
      if (element) {
        element.style.display = element.classList.contains("bg-gradient-to-br") ? "flex" : "block"
      }
    })

    // Cleanup
    document.body.removeChild(printContent)
    document.head.removeChild(customStyles)
    document.head.removeChild(katexLink)
  }, 500)
}

// Export default object with all functions
export default {
  QUIZ_FORMATS,
  TIME_TO_QUESTIONS,
  MathFormulaParse,
  getFormatConfig,
  createDocumentHeader,
  createStudentInfoSection,
  createInstructionsSection,
  createMetadataSection,
  createAnswerSpace,
  createQuestionElement,
  createDocumentFooter,
  generateFormattedQuiz,
  printFormattedQuiz,
}
